import {
    BATCH_DEBOUNCE_MS,
    ERROR_COOLDOWN_MS,
    TRANSLATABLE_ATTRIBUTES,
    VIEWPORT_MARGIN,
} from '../constants/translationConfig';
import { collectAttributeTargets, collectTextTargets } from '../utils/collectTextTargets';
import { translateTargets } from './translateTargets';

const IDLE = { state: 'idle', message: '' };

/**
 * Translates the page in place, one screenful at a time.
 *
 *  1. It scans the page for text (utils/collectTextTargets) and watches each piece's element
 *     with an IntersectionObserver.
 *  2. When an element comes near the visible screen, its text joins the next batch. Text below
 *     the fold is left alone until the user scrolls to it.
 *  3. A batch is translated (engine/translateTargets: cache first, then the API) and written
 *     back into the page, keeping the original spacing.
 *  4. A MutationObserver picks up content added later (React renders, websocket updates) and
 *     text that React changes in place, so those are translated too.
 *
 * Every translated value is remembered with its original, so nothing is translated twice and
 * the engine ignores the changes it makes itself.
 *
 * @param {object}   options
 * @param {Element}  [options.root=document.body]
 * @param {string}   options.targetLanguage
 * @param {(texts: string[], targetLanguage: string) => Promise<Array<string|null>>} options.translateBatch
 * @param {{ get: Function, setMany: Function }} options.cache
 * @param {(status: { state: 'idle'|'translating'|'error', message: string }) => void} [options.onStatus]
 */
export const createPageTranslator = ({
    root = document.body,
    targetLanguage,
    translateBatch,
    cache,
    onStatus = () => {},
}) => {
    // Text node -> record, and element -> { [attr]: record }.
    // record: { original, applied, state: 'queued' | 'done' }
    const textRecords = new WeakMap();
    const attrRecords = new WeakMap();

    const waiting = new Map(); // element -> Set(targets) not yet near the screen
    const ready = new Set(); // targets near the screen, waiting for the next batch

    let viewportObserver = null;
    let mutationObserver = null;
    let flushTimer = null;
    let cooldownUntil = 0;
    let destroyed = false;
    let busy = false;

    // ── Small helpers ─────────────────────────────────────────────────────────
    const readValue = (target) =>
        target.kind === 'text' ? target.node.nodeValue : target.node.getAttribute(target.attr);

    const writeValue = (target, value) => {
        if (target.kind === 'text') target.node.nodeValue = value;
        else target.node.setAttribute(target.attr, value);
    };

    const getRecord = (target) =>
        target.kind === 'text'
            ? textRecords.get(target.node)
            : attrRecords.get(target.node)?.[target.attr];

    const setRecord = (target, record) => {
        if (target.kind === 'text') {
            textRecords.set(target.node, record);
            return;
        }
        const records = attrRecords.get(target.node) || {};
        records[target.attr] = record;
        attrRecords.set(target.node, records);
    };

    const removeRecord = (target) => {
        if (target.kind === 'text') textRecords.delete(target.node);
        else {
            const records = attrRecords.get(target.node);
            if (records) delete records[target.attr];
        }
    };

    const isStillQueued = (target) => {
        const record = getRecord(target);
        return Boolean(
            record &&
            record.state === 'queued' &&
            record.original === target.text &&
            target.node.isConnected &&
            readValue(target) === target.text
        );
    };

    // ── Scheduling ────────────────────────────────────────────────────────────
    const moveToReady = (element) => {
        const targets = waiting.get(element);
        if (targets) targets.forEach((target) => ready.add(target));
        waiting.delete(element);
    };

    const scheduleFlush = () => {
        if (flushTimer || destroyed) return;
        const delay = Math.max(BATCH_DEBOUNCE_MS, cooldownUntil - Date.now());
        flushTimer = setTimeout(flush, delay); // eslint-disable-line no-use-before-define
    };

    /** Remembers a target and starts watching its element. */
    const register = (targets) => {
        targets.forEach((target) => {
            const record = getRecord(target);
            // Our own translation, or the same original text we are already waiting on.
            if (record && record.applied === target.text) return;
            if (record && record.state === 'queued' && record.original === target.text) return;

            setRecord(target, { original: target.text, applied: null, state: 'queued' });

            if (!waiting.has(target.element)) waiting.set(target.element, new Set());
            waiting.get(target.element).add(target);

            if (viewportObserver) {
                viewportObserver.observe(target.element);
            } else {
                // No IntersectionObserver (very old browser): translate straight away.
                moveToReady(target.element);
                scheduleFlush();
            }
        });
    };

    // ── Writing translations back ─────────────────────────────────────────────
    const applyTranslations = (batch, translations) => {
        batch.forEach((target) => {
            const record = getRecord(target);
            // The page changed this text while we were waiting for the translation: skip it.
            if (!record || record.state !== 'queued' || record.original !== target.text) return;
            if (readValue(target) !== target.text) return;

            const translated = translations.get(target.text.trim());
            if (!translated) {
                removeRecord(target); // no translation: leave the original and allow a later retry
                return;
            }

            const leading = target.text.match(/^\s*/)[0];
            const trailing = target.text.match(/\s*$/)[0];
            writeValue(target, `${leading}${translated}${trailing}`);
            record.applied = readValue(target);
            record.state = 'done';
        });
    };

    /** Stops watching elements that were removed from the page before they were translated. */
    const pruneDetached = () => {
        waiting.forEach((targets, element) => {
            if (element.isConnected) return;
            waiting.delete(element);
            if (viewportObserver) viewportObserver.unobserve(element);
        });
    };

    async function flush() {
        flushTimer = null;
        if (destroyed || busy) return; // a running flush schedules the next one itself

        pruneDetached();
        const batch = Array.from(ready).filter(isStillQueued);
        ready.clear();
        if (batch.length === 0) return;

        busy = true;
        onStatus({ state: 'translating', message: '' });
        try {
            const { translations, error } = await translateTargets({
                targets: batch,
                targetLanguage,
                cache,
                translateBatch,
                isCancelled: () => destroyed,
            });
            if (destroyed) return;

            applyTranslations(batch, translations);

            if (error) {
                cooldownUntil = Date.now() + ERROR_COOLDOWN_MS;
                onStatus({ state: 'error', message: error.message || 'Translation failed.' });
            } else {
                onStatus(IDLE);
            }
        } finally {
            busy = false;
            if (!destroyed && ready.size > 0) scheduleFlush();
        }
    }

    // ── Observers ─────────────────────────────────────────────────────────────
    const handleIntersections = (entries) => {
        let any = false;
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            moveToReady(entry.target);
            viewportObserver.unobserve(entry.target);
            any = true;
        });
        if (any) scheduleFlush();
    };

    const handleMutations = (mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => register(collectTextTargets(node)));
            } else if (mutation.type === 'characterData') {
                register(collectTextTargets(mutation.target));
            } else if (mutation.type === 'attributes') {
                register(collectAttributeTargets(mutation.target));
            }
        });
    };

    // ── Public API ────────────────────────────────────────────────────────────
    const start = () => {
        if (typeof IntersectionObserver !== 'undefined') {
            viewportObserver = new IntersectionObserver(handleIntersections, {
                rootMargin: VIEWPORT_MARGIN,
            });
        }
        mutationObserver = new MutationObserver(handleMutations);
        mutationObserver.observe(root, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: [...TRANSLATABLE_ATTRIBUTES],
        });
        register(collectTextTargets(root));
        onStatus(IDLE);
    };

    const destroy = () => {
        destroyed = true;
        if (flushTimer) clearTimeout(flushTimer);
        flushTimer = null;
        if (viewportObserver) viewportObserver.disconnect();
        if (mutationObserver) mutationObserver.disconnect();
        viewportObserver = null;
        mutationObserver = null;
        waiting.clear();
        ready.clear();
    };

    return { start, destroy };
};
